import React from 'react';
import { Container, Row, Col, Card, Table, Badge, Form, Button, Spinner, Pagination } from 'react-bootstrap';
import {
  ShieldExclamationIcon,
  CheckCircleIcon,
  XCircleIcon,
  FunnelIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  FireIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/solid';
import Header from '../common/Header';
import { useAdminSecurityEvents } from './hooks/useAdminSecurityEvents';

const SEVERITY_BADGES: Record<string, { bg: string; text?: string }> = {
  critical: { bg: 'danger' },
  high: { bg: 'warning', text: 'dark' },
  medium: { bg: 'primary' },
  low: { bg: 'info' },
  info: { bg: 'secondary' },
};

const SEVERITY_ICONS: Record<string, React.ReactNode> = {
  critical: <FireIcon style={{ width: 16, height: 16 }} />,
  high: <ExclamationTriangleIcon style={{ width: 16, height: 16 }} />,
  medium: <ShieldExclamationIcon style={{ width: 16, height: 16 }} />,
  low: <InformationCircleIcon style={{ width: 16, height: 16 }} />,
  info: <InformationCircleIcon style={{ width: 16, height: 16 }} />,
};

const AdminSecurityEvents: React.FC = () => {
  const {
    events, summary, choices, loading,
    totalCount, currentPage, totalPages,
    filters, setFilters,
    expandedId, setExpandedId,
    selectedIds, toggleSelect, toggleSelectAll,
    fetchEvents, fetchSummary, resolveEvent, unresolveEvent, bulkResolve,
    clearFilters, formatDate, formatTimeAgo,
  } = useAdminSecurityEvents();

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    const pages = [];
    const start = Math.max(1, currentPage - 2);
    const end = Math.min(totalPages, currentPage + 2);
    for (let i = start; i <= end; i++) {
      pages.push(
        <Pagination.Item key={i} active={i === currentPage} onClick={() => fetchEvents(i)}>
          {i}
        </Pagination.Item>
      );
    }
    return (
      <Pagination className="mb-0 justify-content-center" size="sm">
        <Pagination.First onClick={() => fetchEvents(1)} disabled={currentPage === 1} />
        <Pagination.Prev onClick={() => fetchEvents(currentPage - 1)} disabled={currentPage === 1} />
        {start > 1 && <Pagination.Ellipsis disabled />}
        {pages}
        {end < totalPages && <Pagination.Ellipsis disabled />}
        <Pagination.Next onClick={() => fetchEvents(currentPage + 1)} disabled={currentPage === totalPages} />
        <Pagination.Last onClick={() => fetchEvents(totalPages)} disabled={currentPage === totalPages} />
      </Pagination>
    );
  };

  return (
    <>
      <Header />
      <Container fluid className="py-4 px-3 px-md-4 page-content-padding">
        {/* Title */}
        <Row className="mb-4">
          <Col>
            <div className="d-flex align-items-center gap-2">
              <ShieldExclamationIcon style={{ width: 28, height: 28, color: '#dc3545' }} />
              <h3 className="mb-0" style={{ fontWeight: 700 }}>Security Events</h3>
            </div>
            <p className="text-muted mt-1 mb-0">Monitor security threats, failed logins, rate limits, and suspicious activity.</p>
          </Col>
        </Row>

        {/* Summary Cards */}
        {summary && (
          <Row className="mb-4 g-3">
            <Col xs={6} md={3} lg={2}>
              <Card className="text-center h-100 border-0 shadow-sm">
                <Card.Body className="py-3">
                  <div style={{ fontSize: '1.8rem', fontWeight: 700, color: '#dc3545' }}>
                    {summary.severity_24h.critical}
                  </div>
                  <small className="text-muted">Critical (24h)</small>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={6} md={3} lg={2}>
              <Card className="text-center h-100 border-0 shadow-sm">
                <Card.Body className="py-3">
                  <div style={{ fontSize: '1.8rem', fontWeight: 700, color: '#ffc107' }}>
                    {summary.severity_24h.high}
                  </div>
                  <small className="text-muted">High (24h)</small>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={6} md={3} lg={2}>
              <Card className="text-center h-100 border-0 shadow-sm">
                <Card.Body className="py-3">
                  <div style={{ fontSize: '1.8rem', fontWeight: 700, color: '#0d6efd' }}>
                    {summary.severity_24h.medium}
                  </div>
                  <small className="text-muted">Medium (24h)</small>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={6} md={3} lg={2}>
              <Card className="text-center h-100 border-0 shadow-sm">
                <Card.Body className="py-3">
                  <div style={{ fontSize: '1.8rem', fontWeight: 700, color: '#e74c3c' }}>
                    {summary.unresolved}
                  </div>
                  <small className="text-muted">Unresolved</small>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={6} md={3} lg={2}>
              <Card className="text-center h-100 border-0 shadow-sm">
                <Card.Body className="py-3">
                  <div style={{ fontSize: '1.8rem', fontWeight: 700 }}>
                    {summary.last_24h}
                  </div>
                  <small className="text-muted">Last 24h</small>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={6} md={3} lg={2}>
              <Card className="text-center h-100 border-0 shadow-sm">
                <Card.Body className="py-3">
                  <div style={{ fontSize: '1.8rem', fontWeight: 700 }}>
                    {summary.last_7d}
                  </div>
                  <small className="text-muted">Last 7 Days</small>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Top IPs + Top Event Types */}
        {summary && (summary.top_ips_24h.length > 0 || summary.top_event_types_24h.length > 0) && (
          <Row className="mb-4 g-3">
            {summary.top_event_types_24h.length > 0 && (
              <Col md={6}>
                <Card className="border-0 shadow-sm h-100">
                  <Card.Header className="bg-white border-bottom">
                    <strong>Top Event Types (24h)</strong>
                  </Card.Header>
                  <Card.Body className="p-0">
                    <Table size="sm" className="mb-0">
                      <tbody>
                        {summary.top_event_types_24h.map((item, i) => (
                          <tr key={i}>
                            <td className="ps-3">
                              {choices.event_types.find(([k]) => k === item.event_type)?.[1] || item.event_type}
                            </td>
                            <td className="text-end pe-3">
                              <Badge bg="dark">{item.count}</Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>
            )}
            {summary.top_ips_24h.length > 0 && (
              <Col md={6}>
                <Card className="border-0 shadow-sm h-100">
                  <Card.Header className="bg-white border-bottom">
                    <strong>Top IPs (24h)</strong>
                  </Card.Header>
                  <Card.Body className="p-0">
                    <Table size="sm" className="mb-0">
                      <tbody>
                        {summary.top_ips_24h.map((item, i) => (
                          <tr key={i}>
                            <td className="ps-3"><code>{item.ip_address}</code></td>
                            <td className="text-end pe-3">
                              <Badge bg="dark">{item.count}</Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>
            )}
          </Row>
        )}

        {/* Filters */}
        <Card className="border-0 shadow-sm mb-4">
          <Card.Body>
            <div className="d-flex align-items-center gap-2 mb-3">
              <FunnelIcon style={{ width: 18, height: 18, color: '#6c757d' }} />
              <strong>Filters</strong>
              <Button variant="outline-secondary" size="sm" className="ms-auto" onClick={clearFilters}>
                Clear
              </Button>
              <Button variant="outline-primary" size="sm" onClick={() => { fetchEvents(1); fetchSummary(); }}>
                <ArrowPathIcon style={{ width: 14, height: 14 }} /> Refresh
              </Button>
              {selectedIds.size > 0 && (
                <Button variant="success" size="sm" onClick={bulkResolve}>
                  <CheckCircleIcon style={{ width: 14, height: 14 }} /> Resolve {selectedIds.size} Selected
                </Button>
              )}
            </div>
            <Row className="g-2">
              <Col xs={6} md={2}>
                <Form.Select
                  size="sm"
                  value={filters.filterSeverity}
                  onChange={e => setFilters(f => ({ ...f, filterSeverity: e.target.value }))}
                >
                  <option value="">All Severities</option>
                  {choices.severities.map(([val, label]) => (
                    <option key={val} value={val}>{label}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs={6} md={2}>
                <Form.Select
                  size="sm"
                  value={filters.filterEventType}
                  onChange={e => setFilters(f => ({ ...f, filterEventType: e.target.value }))}
                >
                  <option value="">All Types</option>
                  {choices.event_types.map(([val, label]) => (
                    <option key={val} value={val}>{label}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs={6} md={2}>
                <Form.Select
                  size="sm"
                  value={filters.filterResolved}
                  onChange={e => setFilters(f => ({ ...f, filterResolved: e.target.value }))}
                >
                  <option value="">All Status</option>
                  <option value="false">Unresolved</option>
                  <option value="true">Resolved</option>
                </Form.Select>
              </Col>
              <Col xs={6} md={2}>
                <Form.Control
                  type="date"
                  size="sm"
                  value={filters.filterDateFrom}
                  onChange={e => setFilters(f => ({ ...f, filterDateFrom: e.target.value }))}
                  placeholder="From"
                />
              </Col>
              <Col xs={6} md={2}>
                <Form.Control
                  type="date"
                  size="sm"
                  value={filters.filterDateTo}
                  onChange={e => setFilters(f => ({ ...f, filterDateTo: e.target.value }))}
                  placeholder="To"
                />
              </Col>
              <Col xs={6} md={2}>
                <Form.Control
                  type="text"
                  size="sm"
                  placeholder="Search IP / username..."
                  value={filters.filterSearch}
                  onChange={e => setFilters(f => ({ ...f, filterSearch: e.target.value }))}
                />
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Events Table */}
        <Card className="border-0 shadow-sm">
          <Card.Body className="p-0">
            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
              </div>
            ) : events.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <ShieldExclamationIcon style={{ width: 48, height: 48, opacity: 0.3 }} />
                <p className="mt-2">No security events found.</p>
              </div>
            ) : (
              <div className="table-responsive">
                <Table hover className="mb-0" size="sm">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 40 }}>
                        <Form.Check
                          type="checkbox"
                          checked={events.filter(e => !e.resolved).length > 0 && events.filter(e => !e.resolved).every(e => selectedIds.has(e.id))}
                          onChange={toggleSelectAll}
                        />
                      </th>
                      <th style={{ width: 90 }}>Severity</th>
                      <th>Event</th>
                      <th>User / IP</th>
                      <th>Time</th>
                      <th style={{ width: 100 }}>Status</th>
                      <th style={{ width: 80 }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map(event => {
                      const sev = SEVERITY_BADGES[event.severity] || { bg: 'secondary' };
                      const isExpanded = expandedId === event.id;
                      return (
                        <React.Fragment key={event.id}>
                          <tr
                            style={{ cursor: 'pointer', opacity: event.resolved ? 0.6 : 1 }}
                            onClick={() => setExpandedId(isExpanded ? null : event.id)}
                          >
                            <td onClick={e => e.stopPropagation()}>
                              {!event.resolved && (
                                <Form.Check
                                  type="checkbox"
                                  checked={selectedIds.has(event.id)}
                                  onChange={() => toggleSelect(event.id)}
                                />
                              )}
                            </td>
                            <td>
                              <Badge bg={sev.bg} text={sev.text as any} className="d-flex align-items-center gap-1" style={{ width: 'fit-content' }}>
                                {SEVERITY_ICONS[event.severity]}
                                {event.severity_display}
                              </Badge>
                            </td>
                            <td>
                              <strong>{event.event_type_display}</strong>
                            </td>
                            <td>
                              <div>{event.username || '—'}</div>
                              <small className="text-muted">{event.ip_address || '—'}</small>
                            </td>
                            <td>
                              <div>{formatTimeAgo(event.timestamp)}</div>
                              <small className="text-muted">{formatDate(event.timestamp)}</small>
                            </td>
                            <td>
                              {event.resolved ? (
                                <Badge bg="success">
                                  <CheckCircleIcon style={{ width: 12, height: 12 }} /> Resolved
                                </Badge>
                              ) : (
                                <Badge bg="danger">
                                  <XCircleIcon style={{ width: 12, height: 12 }} /> Open
                                </Badge>
                              )}
                            </td>
                            <td onClick={e => e.stopPropagation()}>
                              {event.resolved ? (
                                <Button variant="outline-secondary" size="sm" onClick={() => unresolveEvent(event.id)}>
                                  Reopen
                                </Button>
                              ) : (
                                <Button variant="outline-success" size="sm" onClick={() => resolveEvent(event.id)}>
                                  Resolve
                                </Button>
                              )}
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr>
                              <td colSpan={7} className="bg-light">
                                <div className="p-3">
                                  <Row className="g-3">
                                    <Col md={4}>
                                      <small className="text-muted d-block">Path</small>
                                      <code>{event.path || '—'}</code>
                                    </Col>
                                    <Col md={4}>
                                      <small className="text-muted d-block">User Agent</small>
                                      <small style={{ wordBreak: 'break-all' }}>{event.user_agent || '—'}</small>
                                    </Col>
                                    <Col md={4}>
                                      <small className="text-muted d-block">Resolved By</small>
                                      {event.resolved_by_username ? (
                                        <span>{event.resolved_by_username} — {formatDate(event.resolved_at)}</span>
                                      ) : (
                                        <span className="text-muted">—</span>
                                      )}
                                    </Col>
                                    {Object.keys(event.details).length > 0 && (
                                      <Col md={12}>
                                        <small className="text-muted d-block">Details</small>
                                        <pre className="mb-0 bg-white p-2 rounded border" style={{ fontSize: '0.8rem', maxHeight: 200, overflow: 'auto' }}>
                                          {JSON.stringify(event.details, null, 2)}
                                        </pre>
                                      </Col>
                                    )}
                                  </Row>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
          {!loading && totalCount > 0 && (
            <Card.Footer className="bg-white d-flex align-items-center justify-content-between flex-wrap gap-2">
              <small className="text-muted">{totalCount} event{totalCount !== 1 ? 's' : ''}</small>
              {renderPagination()}
            </Card.Footer>
          )}
        </Card>
      </Container>
    </>
  );
};

export default AdminSecurityEvents;
